// The editor's « Modèles » (design system v2) — worker routes/themeEditor.ts.
// A file of its own so parallel work on lib/api.ts never collides.
import { apiFetch } from './api';

/** A themed page as the worker builds it (lib/themes/expand.ts ThemedPage). */
export interface EditorThemedPage {
  page: number;
  layout: '1-up';
  blank: true;
  blank_color: string;
  photos: string[];
  background: { imageUrl?: string; color: string };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  elements: Array<any>;
  theme: string;
  model: string;
  colourway: string;
  side: 'left' | 'right';
  simple?: boolean;
  /** One half of a two-page model (S5 « Panorama »): the photo runs on across the spine. */
  spreadHalf?: 'left' | 'right';
}

/** A two-page model (S5 « Panorama ») in the picker's « Double page » tab — 2026-10-18. */
export interface EditorSpreadCard {
  code: string;
  name: string;
  kind: 'spread';
  style: string;
  count: number;
  /** The spread it would take: this page and the one facing it (album page numbers). */
  left_page: number;
  right_page: number;
  ok: boolean;
  left?: EditorThemedPage;
  right?: EditorThemedPage;
  photo_ids?: string[];
  reason?: 'face' | 'count' | 'model';
  message?: string;
}

export interface EditorModelCard {
  code: string;
  name: string;
  kind: 'simple' | 'decorated';
  style: string;
  count: number;
  favourite: boolean;
  ok: boolean;
  /** Drawn with the album's own photos (ok). */
  page?: EditorThemedPage;
  /** Its photos in slot order (ok). */
  photo_ids?: string[];
  /** Why there is no page: a face would be cut, not enough photos… (not ok) */
  reason?: 'face' | 'count' | 'model';
  message?: string;
}

export interface EditorModelsResponse {
  theme: { slug: string; name: string };
  theme_source: 'chosen' | 'album' | 'cover' | 'fallback';
  colourway: { id: string; name: string; paper: string };
  side: 'left' | 'right';
  /** Staff only: every theme (a client stays in her album's). */
  themes: Array<{ slug: string; name: string }>;
  models: EditorModelCard[];
  /** Two-page models across this page and the one facing it (empty: no facing page, or it is a special page). */
  spreads?: EditorSpreadCard[];
}

export interface EditorModelsRequest {
  /** Album page number (1-based). */
  page: number;
  /** In order: the page's photos, then the photos placed nowhere, then those other pages may lend. */
  pool_ids: string[];
  theme?: string | null;
  side?: 'left' | 'right';
  /** Decoration images the OTHER pages already carry (an object max twice per album). */
  used_images?: string[];
  album_pages?: number;
}

export interface EditorApplyRequest {
  page: number;
  model: string;
  /** Slot order. */
  photo_ids: string[];
  theme?: string | null;
  side?: 'left' | 'right';
  used_images?: string[];
  album_pages?: number;
}

export interface EditorApplyResult {
  page: EditorThemedPage;
  warnings: string[];
  /** A two-page model: its halves and the album pages they replace. */
  spread?: { left: EditorThemedPage; right: EditorThemedPage; left_page: number; right_page: number };
}

/** A refusal the client reads (422: a face would be cut / the wrong number of photos). */
export class ModelRefusal extends Error {
  readonly reason: string;
  constructor(reason: string, message: string) { super(message); this.reason = reason; }
}

export const editorModelsApi = {
  models: (sessionId: string, body: EditorModelsRequest) =>
    apiFetch<EditorModelsResponse>(`/themes/editor/${encodeURIComponent(sessionId)}/models`, { method: 'POST', body: JSON.stringify(body) }),

  /** The themed page (and, for a two-page model, both halves), or a ModelRefusal with the worker's French message. */
  apply: async (sessionId: string, body: EditorApplyRequest): Promise<EditorApplyResult> => {
    try {
      return await apiFetch<EditorApplyResult>(`/themes/editor/${encodeURIComponent(sessionId)}/apply`, { method: 'POST', body: JSON.stringify(body) });
    } catch (e) {
      const b = (e as { body?: { error?: string; message?: string } }).body;
      if (b?.message) throw new ModelRefusal(b.error ?? 'refused', b.message);
      throw e;
    }
  },
};
